import React from 'react';
import { bool, func, number, oneOf, string } from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { I18n as I } from 'react-redux-i18n';
import { LoaderOverlay } from 'vdlib';
import fileIcon from 'static/img/file-icon-48.png';
import photoIcon from 'static/img/photo-icon-48.png';
import successIcon from 'static/img/check-icon-48.png';
import errorIcon from 'static/img/warn-icon-48.png';

const acceptTypes = {
  images: 'image/jpg, image/jpeg, image/png, image/bmp, image/tiff',
};

const typeIcons = {
  file: fileIcon,
  image: photoIcon,
};

const loaderStyle = {
  position: 'relative',
  marginBottom: '30px',
};

const errorStyle = {
  margin: '10px 0 0',
  padding: '5px',
};

const Dropzone = ({
  accept: acceptProp,
  children,
  className,
  current,
  error,
  loading,
  maxFilesNumber,
  maxFilesReached,
  messageClassName,
  onDrop,
  success,
  total,
  type,
}) => {
  const accept = React.useMemo(
    () => acceptTypes[acceptProp] || acceptProp,
    [acceptProp]
  );

  const handleDrop = React.useCallback(
    (acceptedFiles, fileRejections) => {
      if (fileRejections.length === 0) onDrop(acceptedFiles);
    },
    [onDrop]
  );

  const {
    fileRejections,
    getRootProps,
    getInputProps,
    isDragReject,
    isDragActive,
  } = useDropzone({
    accept,
    onDrop: handleDrop,
  });

  return (
    <div>
      <div
        {...getRootProps()}
        className={`dropzone ${className || ''}`}
        //style={dropzoneStyle}
      >
        <input {...getInputProps()} />
        <div
          className={`drop-message-wrap ${isDragActive ? 'active' : ''} ${
            messageClassName || ''
          }`}
        >
          <div className="drop-message">
            <div style={loading ? loaderStyle : {}}>
              <LoaderOverlay loading={loading} />
            </div>

            {(fileRejections.length > 0 && !isDragActive) || isDragReject ? (
              <div className="default-drop-message">
                <img src={errorIcon} alt="" />
                <br />
                {I.t('CAN_ONLY_UPLOAD_FILES')}{' '}
                {accept
                  .split(',')
                  .map(
                    (t, i, arr) =>
                      (i === arr.length - 1 ? ' και ' : '') + t.split('/')[1]
                  )
                  .join(', ')}
              </div>
            ) : !!children ? (
              children
            ) : loading === true ? (
              <div className="default-drop-message">
                {I.t('UPLOADING')}
                <br />
                {current} {I.t('FROM')} {total}
              </div>
            ) : maxFilesReached === true ? (
              <div className="default-drop-message">
                <img src={errorIcon} alt="" />
                <br />
                {I.t('MAX_LIMIT_OF_PHOTOS_REACHED')} {maxFilesNumber}
              </div>
            ) : success === true ? (
              <div className="default-drop-message">
                <img src={successIcon} alt="" />
                <br />
                {I.t('UPLOAD_COMPLETE')}
              </div>
            ) : (
              <div className="default-drop-message">
                <img src={typeIcons[type || 'file']} alt="" />
                <br />
                {I.t('DRAG_FILES_HERE')} {I.t('OR_CLICK_TO_UPLOAD')}
                <div className="caption bold" style={{ whiteSpace: 'nowrap' }}>
                  {I.t('MAX_FILE_SIZE')} 10MB
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* </ReactDropzone> */}

      {error && (
        <div className="row alert-danger" style={errorStyle}>
          {typeof error === 'string'
            ? error
            : `${I.t('SOMETHING_WENT_WRONG')}. ${I.t(
                'PLEASE_CHECK_FILE_SIZE'
              )}`}{' '}
        </div>
      )}
    </div>
  );
};

Dropzone.propTypes = {
  accept: string,
  className: string,
  current: number,
  error: string,
  loading: bool,
  maxFilesReached: bool,
  maxFilesNumber: number,
  messageClassName: string,
  onDrop: func.isRequired,
  success: bool,
  total: number,
  type: oneOf(['file', 'image']),
};

export default Dropzone;
